import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Music, CheckCircle, XCircle, Clock, Eye,
  ChevronRight, Search, Loader2,
  Mail, Play, ExternalLink, X
} from 'lucide-react';
import { getImageUrl } from '../../utils/api';
import './StaffReviewStation.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFileUrl = (url) => {
  if (!url) return '#';
  return getImageUrl(url) || '#';
};

const StaffReviewStation = () => {
  const [loading, setLoading] = useState(true);
  const [demos, setDemos] = useState([]);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const getToken = () => localStorage.getItem('adminToken');
  const getHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  useEffect(() => { fetchDemos(); }, [filterStatus]);

  const fetchDemos = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/demos${filterStatus ? `?status=${filterStatus}` : ''}`,
        { headers: getHeaders() }
      );
      if (res.ok) setDemos(await res.json());
    } catch {
      toast.error('Failed to load demos');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewDemo = async (demoId, status, notes) => {
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/demos/${demoId}/review?status=${status}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`,
        { method: 'PUT', headers: getHeaders() }
      );
      const data = await res.json();
      if (res.ok) {
        if (status === 'accepted' && data.registration_url) {
          toast.success('Demo accepted! Invite link created.', { duration: 8000 });
          try { await navigator.clipboard.writeText(data.registration_url); toast.info('Registration link copied!', { duration: 5000 }); }
          catch { toast.info(`Registration URL: ${data.registration_url}`, { duration: 15000 }); }
        } else {
          toast.success(`Demo marked as ${status}`);
        }
        if (data.warning) toast.warning(data.warning, { duration: 8000 });
        fetchDemos();
        setSelectedDemo(null);
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch {
      toast.error('Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredDemos = demos.filter(d =>
    d.artist_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.genre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'st-pending';
      case 'accepted': case 'approved': return 'st-accepted';
      case 'under_review': return 'st-review';
      case 'rejected': return 'st-rejected';
      default: return '';
    }
  };

  return (
    <div className="staff-review-station" data-testid="staff-review-station">
      <div className="srs-header">
        <h1>Demo Submissions</h1>
        <p>Review incoming demo submissions from artists</p>
      </div>

      <div className="srs-toolbar">
        <div className="srs-search">
          <Search size={16} />
          <input type="text" placeholder="Search demos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-input" />
        </div>
        <div className="srs-filters">
          {[
            { key: '', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'under_review', label: 'In Review' },
            { key: 'accepted', label: 'Accepted' },
            { key: 'rejected', label: 'Rejected' },
          ].map(f => (
            <button key={f.key} className={`srs-filter-btn${filterStatus === f.key ? ' active' : ''}`} onClick={() => setFilterStatus(f.key)} data-testid={`filter-${f.key || 'all'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="srs-split">
        {/* List panel */}
        <div className="srs-list">
          {loading ? (
            <div className="srs-empty"><Loader2 size={24} className="spinner" /><p>Loading...</p></div>
          ) : filteredDemos.length === 0 ? (
            <div className="srs-empty"><Music size={40} /><p>No demos found</p></div>
          ) : (
            filteredDemos.map(demo => (
              <div
                key={demo.id}
                className={`srs-item${selectedDemo?.id === demo.id ? ' selected' : ''}`}
                onClick={() => setSelectedDemo(demo)}
                data-testid={`demo-item-${demo.id}`}
              >
                <div className="srs-item-info">
                  <h4>{demo.artist_name}</h4>
                  <span className="srs-item-genre">{demo.genre}</span>
                  <span className="srs-item-email">{demo.email}</span>
                </div>
                <div className="srs-item-meta">
                  <span className={`srs-status ${getStatusClass(demo.status)}`}>{demo.status?.replace('_', ' ')}</span>
                  <span className="srs-item-date">{formatDate(demo.created_at)}</span>
                </div>
                <ChevronRight size={16} className="srs-chevron" />
              </div>
            ))
          )}
        </div>

        {/* Detail panel */}
        <div className={`srs-detail${selectedDemo ? ' open' : ''}`}>
          {selectedDemo ? (
            <DemoDetail
              demo={selectedDemo}
              onReview={handleReviewDemo}
              onClose={() => setSelectedDemo(null)}
              loading={actionLoading}
              formatDate={formatDate}
              getStatusClass={getStatusClass}
            />
          ) : (
            <div className="srs-empty-detail"><Music size={40} /><p>Select a demo to review</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

const DemoDetail = ({ demo, onReview, onClose, loading, formatDate, getStatusClass }) => {
  const [notes, setNotes] = useState(demo.staff_notes || '');

  useEffect(() => { setNotes(demo.staff_notes || ''); }, [demo.id, demo.staff_notes]);

  return (
    <>
      <button className="srs-close" onClick={onClose}><X size={18} /></button>

      <div className="srs-detail-header">
        <h2>{demo.artist_name}</h2>
        <span className={`srs-status large ${getStatusClass(demo.status)}`}>{demo.status?.replace('_', ' ')}</span>
      </div>

      {/* Artist Info Card */}
      <div className="srs-card">
        <h3>Artist Info</h3>
        <div className="srs-info-row"><Mail size={14} /> <span>{demo.email}</span></div>
        <div className="srs-info-row"><Music size={14} /> <span>Genre: {demo.genre}</span></div>
        <div className="srs-info-row"><Clock size={14} /> <span>Submitted: {formatDate(demo.created_at)}</span></div>
      </div>

      {/* Demo Link Card */}
      <div className="srs-card">
        <h3>Demo Link</h3>
        <a href={getFileUrl(demo.demo_link)} target="_blank" rel="noopener noreferrer" className="srs-demo-btn" data-testid="listen-demo-btn">
          <Play size={16} /> Listen to Demo <ExternalLink size={12} />
        </a>
      </div>

      {/* SoundCloud Tracks — embedded players with fallback links */}
      {demo.soundcloud_tracks?.length > 0 && (
        <div className="srs-card">
          <h3>SoundCloud Tracks</h3>
          {demo.soundcloud_tracks.map((trackUrl, i) => {
            const isSoundCloud = trackUrl && trackUrl.includes('soundcloud.com/');
            return (
              <div key={i} className="srs-sc-embed" data-testid={`sc-embed-${i}`} style={{ marginBottom: 14 }}>
                {isSoundCloud && (
                  <iframe
                    title={`SoundCloud Track ${i + 1}`}
                    width="100%"
                    height="166"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(trackUrl)}&color=%2300ffff&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
                    style={{ borderRadius: 6, marginBottom: 6 }}
                  />
                )}
                <a href={trackUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--neon-cyan)', fontSize: '0.75rem', textDecoration: 'none', wordBreak: 'break-all' }}>
                  <ExternalLink size={12} /> {trackUrl}
                </a>
              </div>
            );
          })}
        </div>
      )}

      {/* Social Links */}
      {demo.social_links && (
        <div className="srs-card">
          <h3>Social Links</h3>
          <p className="srs-text">{demo.social_links}</p>
        </div>
      )}

      {/* Message */}
      {demo.message && (
        <div className="srs-card">
          <h3>Message</h3>
          <p className="srs-text">{demo.message}</p>
        </div>
      )}

      {/* Staff Notes */}
      <div className="srs-card">
        <h3>Staff Notes</h3>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Add notes about this submission..."
          rows={3}
          className="srs-notes"
          data-testid="staff-notes"
        />
      </div>

      {/* Action buttons */}
      {(demo.status === 'pending' || demo.status === 'under_review') && (
        <div className="srs-actions">
          {demo.status === 'pending' && (
            <button className="srs-action-btn review" onClick={() => onReview(demo.id, 'under_review', notes)} disabled={loading} data-testid="btn-under-review">
              <Eye size={16} /> Mark Under Review
            </button>
          )}
          <button className="srs-action-btn accept" onClick={() => onReview(demo.id, 'accepted', notes)} disabled={loading} data-testid="btn-accept">
            {loading ? <Loader2 size={16} className="spinner" /> : <CheckCircle size={16} />} Accept & Create Profile
          </button>
          <button className="srs-action-btn reject" onClick={() => onReview(demo.id, 'rejected', notes)} disabled={loading} data-testid="btn-reject">
            <XCircle size={16} /> Reject
          </button>
        </div>
      )}

      {demo.reviewed_by && (
        <p className="srs-reviewed-info">Reviewed by {demo.reviewed_by} on {formatDate(demo.reviewed_at)}</p>
      )}
    </>
  );
};

export default StaffReviewStation;
